<script lang="ts">
  import Button from '$components/elements/Button.svelte';
  import Icon from '$components/elements/Icon.svelte';
  import Input from '$components/elements/Input.svelte';
  import Modal from '$components/elements/Modal.svelte';
  import ModalImageHeader from '$components/elements/ModalImageHeader.svelte';
  import Grid from '$components/layout/Grid.svelte';
  import { currentUser, pbClient } from '$lib/client/pocketbase';
  import { requestPasswordReset, updateUserData } from '$lib/client/pocketbase/users';
  import { globalError, showDashboard, showDashboardSettings, showEmailBlocker } from '$lib/stores';
  import { onMount } from 'svelte';

  let name = '';
  let surname = '';
  let address = '';
  let phone = '';
  let email = '';

  let loading = false;

  let page: 'account' | 'password' = 'account';

  function setValues() {
    if ($currentUser) {
      name = $currentUser.name;
      surname = $currentUser.surname;
      address = $currentUser.address;
      phone = $currentUser.phone;
      email = $currentUser.email;
    }
  }

  onMount(() => {
    setValues();
  });

  pbClient.authStore.onChange(() => {
    setValues();
  });

  async function submit() {
    loading = true;

    if ($currentUser) {
      const response = await updateUserData({
        name,
        surname,
        address,
        phone,
        email,
        username: $currentUser.username,
        id: $currentUser.id
      });

      if (response === null) {
        showDashboardSettings.set(false);
        globalError.set('Nastala chyba při ukládání dat. Zkuste to prosím znovu.');
      }
    }

    loading = false;
  }
</script>

<Modal
  width="1096px"
  showModal={$showDashboardSettings}
  onClose={() => showDashboardSettings.set(false)}
>
  <div class="dashboard-settings">
    <ModalImageHeader>
      <div
        class="dashboard-settings__settings-button"
        on:click={() => {
          showDashboardSettings.set(false);
          showDashboard.set(true);
        }}
        on:keypress={() => {
          showDashboardSettings.set(false);
          showDashboard.set(true);
        }}
      >
        <Icon icon="arrow-left" viewBox={15} />
        <div class="dashboard-settings__settings-button__text">Zpět</div>
      </div>
      <h1>Nastavení osobních údajů</h1>
    </ModalImageHeader>

    {#if page === 'account'}
      <form on:submit|preventDefault={submit}>
        <Grid
          breakpoints={{
            0: {
              templateColumns: '1fr',
              margin: '37px 16px',
              width: 'calc(100% - 32px)',
              gap: '30px'
            },
            720: {
              templateColumns: '1fr 1fr',
              gap: {
                column: '36px',
                row: '30px'
              },
              margin: '37px 90px',
              width: 'calc(100% - 180px)',
              alignItems: 'start'
            }
          }}
        >
          <Input name="name" label="Jméno" bind:value={name} required />
          <Input name="surname" label="Příjmení" bind:value={surname} required />
          <Input name="email" type="email" label="Email" value={email} disabled />
          <Input
            name="phone"
            type="tel"
            label="Telefon"
            autocomplete="tel"
            bind:value={phone}
            required
          />
          <Input name="address" label="Adresa" bind:value={address} required />
        </Grid>
        <Grid
          breakpoints={{
            0: {
              templateColumns: '1fr',
              margin: '37px 16px',
              width: 'calc(100% - 32px)',
              gap: '16px'
            },
            720: {
              templateColumns: '1fr 1fr 1fr',
              margin: '37px 90px 0',
              width: 'calc(100% - 180px)'
            }
          }}
        >
          <div class="dashboard-settings__primary-button">
            <Button
              {loading}
              onFinished={() => {
                loading = false;
              }}
              submit
              width="200px">Uložit</Button
            >
          </div>
          <div class="dashboard-settings__secondary-button">
            <Button
              slimness="super-slim"
              border="1px solid #1d1d1b"
              textColor="#1d1d1b"
              color="transparent"
              on:click={() => {
                page = 'password';
              }}
            >
              Změna hesla
            </Button>
          </div>
        </Grid>
      </form>
    {:else}
      <h2>
        Po stisknutí tlačítka se prosím přesuňte do své e-mailové schránky,<br />kam Vám přijdou
        další pokyny pro změnu hesla.
      </h2>
      <Grid
        breakpoints={{
          0: {
            templateColumns: '1fr',
            margin: '37px 16px',
            width: 'calc(100% - 32px)',
            gap: '16px'
          },
          720: {
            templateColumns: '1fr 1fr 1fr',
            margin: '37px 90px 0',
            width: 'calc(100% - 180px)'
          }
        }}
      >
        <div class="dashboard-settings__primary-button">
          <Button
            {loading}
            width="200px"
            on:click={async () => {
              if (!$currentUser) {
                showDashboardSettings.set(false);
                globalError.set('Nastala chyba při odesílání e-mailu. Zkuste to prosím znovu.');
                return;
              }
              loading = true;
              await requestPasswordReset($currentUser.email);
              loading = false;
            }}
            onFinished={() => {
              showDashboardSettings.set(false);
              showEmailBlocker.set({
                type: 'password-reset',
                email: $currentUser?.email
              });
            }}
          >
            Změnit heslo
          </Button>
        </div>
        <div class="dashboard-settings__secondary-button">
          <Button
            slimness="super-slim"
            border="1px solid #1d1d1b"
            textColor="#1d1d1b"
            color="transparent"
            on:click={() => {
              page = 'account';
            }}
          >
            Nastavení profilu
          </Button>
        </div>
      </Grid>
    {/if}
  </div>
</Modal>

<style lang="scss">
  .dashboard-settings {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: 100%;
    padding-bottom: 60px;
    position: relative;
    h1 {
      font-weight: 700;
      font-size: 28px;
      line-height: 38px;
      text-align: center;
    }
    h2 {
      font-weight: 400;
      font-size: 18px;
      line-height: 1.6;
      max-width: 60%;
      text-align: center;
      margin: 64px auto 32px;
    }

    form {
      width: 100%;
    }

    &__settings-button {
      position: absolute;
      top: 20px;
      left: 25px;
      display: flex;
      align-items: center;
      gap: 12px;
      cursor: pointer;
      overflow: hidden;
      transition: 0.2s ease all;
      &__text {
        transition: 0.2s ease all;
        opacity: 0;
      }
      &:hover {
        .dashboard-settings {
          &__settings-button {
            &__text {
              opacity: 1;
            }
          }
        }
      }
    }
    &__primary-button {
      justify-self: center;
      grid-column: 2;
    }
    &__secondary-button {
      justify-self: end;
    }
  }

  @include mobile {
    .dashboard-settings {
      &__settings-button {
        top: 16px;
        left: 16px;
        &__text {
          opacity: 1;
        }
      }
      &__primary-button {
        grid-column: 1;
      }
      &__secondary-button {
        justify-self: center;
      }
    }
  }
</style>
